import { FC } from 'react';
import { useDrawerStore } from './useDrawerStore';

/**
 * All drawers should have width 420px.
 */
type AllowedWidth = 420 | 240 | 620;

type CreateDrawerOptions<T extends string, P> = {
  name: T;
  slot: 'left' | 'right';
  width: AllowedWidth;
  drawerId: (props: P) => string | number | undefined;
};

type CreateDrawerReturn<T extends string, P extends object | void = void> = {
  [K0 in `toggle${T}`]: P extends object ? (props: P) => boolean : () => boolean;
} & {
  [K1 in `close${T}`]: () => boolean;
} & {
  [K2 in `useIsOpen${T}`]: () => P extends object ? P | null : boolean;
} & {
  [K3 in `updateProps${T}`]: (props: P) => void;
} & {
  [K4 in `open${T}`]: P extends object ? (props: P) => boolean : () => boolean;
};

export const createDrawer = <T extends string, P extends object | void = void>(
  Component: FC<P>,
  { slot, name, width }: CreateDrawerOptions<T, P>,
): CreateDrawerReturn<T, P> => {
  const useIsOpen = () =>
    useDrawerStore((state) => {
      const isOpen = state[slot].width > 0 && state[slot].name === name;

      if (isOpen) {
        return state[slot].props ?? true;
      }

      return null;
    });

  const toggle = (props: P) =>
    !!useDrawerStore?.getState().onToggleDrawer(slot, {
      width,
      name,
      Component,
      props: props,
    });

  const close = () => {
    useDrawerStore?.getState().onCloseDrawer(name);
  };

  const updateProps = (props: P) => {
    const state = useDrawerStore?.getState();

    if (state && state[slot].name === name) {
      state.onOpenDrawer(slot, {
        width,
        name,
        props,
        Component,
      });
    }
  };

  const open = (props: P) =>
    useDrawerStore.getState().onOpenDrawer(slot, {
      width,
      name,
      Component,
      props,
    });

  return {
    [`toggle${name}`]: toggle,
    [`close${name}`]: close,
    [`useIsOpen${name}`]: useIsOpen,
    [`updateProps${name}`]: updateProps,
    [`open${name}`]: open,
  } as CreateDrawerReturn<T, P>;
};
